import {Checkbox, CircularProgress} from '@mui/material'
import {GridRowId} from '@mui/x-data-grid-pro'
import {envImagePath} from 'GLOBAL/envVariables'

interface Props {
  rows: any[]
  loading: boolean
  selectionModel: GridRowId[]
  onSelectionChange: (ids: GridRowId[]) => void
  mode: 'check' | 'assign'
  onImageDetailOpen: () => void
  onDeviceDetailOpen: () => void
  setDeviceDetailData: (data: any) => void
}

const checkStatusStyle: Record<string, string> = {
  pass: 'text-green-600 font-semibold',
  fail: 'text-red-500 font-semibold',
  none: 'text-gray-400',
}

const assignStatusStyle: Record<string, string> = {
  assigned: 'text-blue-600 font-semibold',
  unassigned: 'text-gray-400',
}

const DeviceAllocationMobileCard = ({
  rows,
  loading,
  selectionModel,
  onSelectionChange,
  mode,
  onImageDetailOpen,
  onDeviceDetailOpen,
  setDeviceDetailData,
}: Props) => {
  const isSelected = (id: GridRowId) => selectionModel.includes(id)

  const toggleSelect = (id: GridRowId) => {
    const newIds = isSelected(id)
      ? selectionModel.filter((s) => s !== id)
      : [...selectionModel, id]
    onSelectionChange(newIds)
  }

  if (loading) {
    return (
      <div className='flex justify-center p-8'>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-2 mt-2'>
      {rows.map((row) => {
        const selected = isSelected(row.id)
        const imageSrc = row.filePath ? `${envImagePath}${row.filePath}` : null

        const checkStatusKey =
          typeof row.checkStatus === 'string' ? row.checkStatus.toLowerCase() : null
        const checkStatusLabel =
          checkStatusKey === 'pass' ? 'PASS' : checkStatusKey === 'fail' ? 'FAIL' : null
        const checkStatusClass = checkStatusKey ? checkStatusStyle[checkStatusKey] : ''

        const assignStatusKey =
          typeof row.assignStatus === 'string' ? row.assignStatus.toLowerCase() : null
        const assignStatusLabel =
          assignStatusKey === 'assigned'
            ? '할당'
            : assignStatusKey === 'unassigned'
            ? '미할당'
            : null
        const assignStatusClass = assignStatusKey ? assignStatusStyle[assignStatusKey] : ''

        return (
          <div
            key={row.id}
            className={`border rounded-lg p-3 flex gap-2 shadow-sm ${
              selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
            }`}
          >
            <div className='flex items-start pt-1 shrink-0'>
              <Checkbox checked={selected} onChange={() => toggleSelect(row.id)} size='small' />
            </div>

            <div className='flex-1 min-w-0 flex flex-col gap-1'>
              {/* 시리얼번호 + 상태 텍스트 */}
              <div className='flex items-center gap-2 flex-wrap'>
                <span
                  className='text-blue-600 font-bold cursor-pointer text-lg'
                  onClick={() => {
                    setDeviceDetailData(row)
                    onDeviceDetailOpen()
                  }}
                >
                  {row.serialNumber ?? '-'}
                </span>
                {checkStatusLabel && (
                  <span className={`text-sm ${checkStatusClass}`}>{checkStatusLabel}</span>
                )}
                {mode === 'assign' && assignStatusLabel && (
                  <span className={`text-sm ${assignStatusClass}`}>{assignStatusLabel}</span>
                )}
              </div>

              {/* 단말기 타입 */}
              <div className='text-base text-gray-600'>{row.deviceTypeName ?? '-'}</div>

              {/* IMEI */}
              <div className='text-base text-gray-500'>IMEI: {row.imei ?? '-'}</div>

              {/* 검수자 */}
              {row.checkName && (
                <div className='text-base text-gray-500'>검수자: {row.checkName}</div>
              )}

              {/* 할당 대상 (할당탭) */}
              {mode === 'assign' && row.assignToName && (
                <div className='text-base text-gray-500'>
                  할당 대상: {row.assignToName} ({row.assignToId})
                </div>
              )}

              {/* 배터리 + 온도 + 펌웨어 */}
              <div className='flex items-center gap-3 mt-1 text-base flex-wrap'>
                {row.batteryLevel != null && (
                  <span
                    className={`font-semibold ${
                      row.batteryLevel < 30 ? 'text-red-500' : 'text-gray-500'
                    }`}
                  >
                    배터리 {row.batteryLevel}%
                  </span>
                )}
                {row.temperature != null && row.temperature < 90 && (
                  <span className='text-gray-400'>{row.temperature}°C</span>
                )}
                {row.firmwareVersion && (
                  <span className='text-gray-400'>FW: {row.firmwareVersion}</span>
                )}
              </div>
            </div>

            {/* 이미지 썸네일 */}
            {imageSrc && (
              <div className='shrink-0 self-center' style={{width: '40%', maxWidth: 160}}>
                <img
                  src={imageSrc}
                  alt='device'
                  className='w-full h-auto object-contain rounded cursor-pointer border-2 border-gray-400'
                  onClick={() => {
                    setDeviceDetailData(row)
                    onImageDetailOpen()
                  }}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default DeviceAllocationMobileCard
